import {
  CT_PDT_EVENTS,
  LISTING_PDT_EVENTS,
  LISTING_PDT_CLICK_EVENTS,
  TRAVELLER_PDT_EVENTS,
  getPdtSchemaId,
  getPdtSchemaName,
  pdtCtEventValues,
} from './PdtCtConstants';
import { logRailsEventToPDT, getJourneyId, buildEnrichedSearchContext, transformPayloadForXDM } from '../PdtV2Utils';
import { COMMON_EVENT_TRACKING_CONTEXT, COMMON_PDT_CONSTANTS, EXPERIMENT_DETAILS_CONTEXT } from '../CommonPdtConstants';
import { generateUUID } from '@mmt/navigation/src/util';
import { getDateYYYYMMDD } from '@mmt/legacy-commons/Common/utils/DateUtils';
import {
  getRailsCtPdtLoggingOnOff,
  getRailsListingNewPdtLoggingOnOff,
} from 'apps/rails/src/RailsAbConfig';
import { getConfigStore } from 'apps/rails/src/configStore/Common/RailsConfigStoreUtils';
import { configKeys } from 'apps/rails/src/configStore/Common/constants';
import { logGrafanaErrorMetrics } from 'apps/rails/src/Utils/RailsGrafanaTracker';
import { getDataFromStorage } from '@mmt/legacy-commons/AppState/LocalStorage';
import { 
  TRAVELLER_PDT_ADDON_DETAILS,
  LISTING_PDT_TRAIN_CARD_DETAILS,
  ENTITY,
  CURRENCY_INR,
  PRODUCT_TYPE_TRAIN,
  LOB_RAILS,
  ADDON_TYPE_FC,
  ADDON_TYPE_TG,
  ADDON_TYPE_BNPP,
  ADDON_TYPE_FREE_CANCELLATION,
  ADDON_TYPE_TRIP_GUARANTEE,
  UNIT_TYPE_FREE_CANCELLATION,
  UNIT_TYPE_TRIP_GUARANTEE,
  HEADING_FREE_CANCELLATION,
  HEADING_TRIP_GUARANTEE,
  COMPONENT_TYPE_MMT_LISTING_CAROUSEL_DATA,
  PAGE_LOAD_COMPONENT_TYPE,
  LISTING_PAGE_LOAD_ID,
  PAGE_TYPE_FUNNEL,
  PAGE_NAME_LANDING,
  PAGE_NAME_LISTING,
  PAGE_NAME_TRAVELLER,
} from 'apps/rails/src/Utils/RailsConstant';
import { ENTITY_TYPE, PAGE, NEW_PDT_ERROR_CODES } from 'apps/rails/src/types/grafanaTracking.types';
import { XDMWrapper, TARGETS } from '@gt-common/event-logger-web';
import { RailsLandingPageSchema } from '../../../schemas/railsSchemas';

export type eventValue =
  | 'EXPANDED'
  | 'INTERACTED'
  | 'TRAIN'
  | 'BUS'
  | 'LANDING_LOAD'
  | 'LISTING_LOAD'
  | 'THANKYOU_LOAD';

export const logListingPageLoadEvent = async (
  eventValue: eventValue,
  tbsTrainList: Array<object>,
  connectedTravelData: object,
  fcInterventionData: object,
  searchContext: object,
  filterObject?: any,
) => {
  try {
    if (!getRailsListingNewPdtLoggingOnOff()) {
      return null;
    }

    const productList = tbsTrainList?.map((train, id) => {
      const trainData = {
        trainMetaData: train,
      };
      const legs = [];
      legs.push(getCtTrainsLegData(trainData, id + 1));
      return {
        id: `${train?.frmStnCode}_${train?.toStnCode}_${getDateYYYYMMDD(
          train?.departureDateAndTime,
        )}_${train?.trainNumber}`,
        product_type: PRODUCT_TYPE_TRAIN,
        duration: train?.duration || null,
        rank: id + 1,
        legs,
      };
    });

    const connectedTravelProductList = constructConnectedTravelPdtData(
      connectedTravelData?.connectedTravelOptions,
      eventValue,
    );

    if (connectedTravelProductList?.length > 0) {
      productList.push(...connectedTravelProductList);
    }

    const mmtlistingHighlightsData = await getConfigStore(
      configKeys.RAILS_LISTING_PAGE_MMT_HIGHLIGHTS,
    );

    const carouselData = mmtlistingHighlightsData?.mmtlistingHighlights;

    const availableAddons = [ADDON_TYPE_BNPP, ADDON_TYPE_TG, ADDON_TYPE_FC];
    const contentDetails = carouselData
      ?.filter((carouselAddOn) => availableAddons.includes(carouselAddOn?.code))
      ?.map((carouselAddOn, index) => {
        return {
          id: carouselAddOn?.code,
          type: COMPONENT_TYPE_MMT_LISTING_CAROUSEL_DATA,
          name: carouselAddOn?.title,
          category_id: carouselAddOn?.code,
          category_name: carouselAddOn?.description,
          position: { v: 0, h: index },
        };
      }) || [];

    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...LISTING_PDT_EVENTS?.[eventValue],
      components: {
        id: LISTING_PAGE_LOAD_ID,
        component_type: PAGE_LOAD_COMPONENT_TYPE,
        content_details: contentDetails,
        product_list: productList,
        filters: {
          seen: buildFiltersSeenArray(filterObject),
        },
      },
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: PAGE_NAME_LISTING,
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: getPdtSchemaId(),
        topic_name: getPdtSchemaName(),
        ...(await getJourneyId(searchContext)),
      },
      search_context: searchContextMapper(searchContext),
      event_detail: eventDetail,
    };
    logRailsEventToPDT(pdtLoggerObj);

    if (eventValue === 'LISTING_LOAD') {
        try {
          const freeCancellationEnabled = (fcInterventionData as any)?.isFreeCancellationEnabled || false;
          
          const hasTripGuarantee = tbsTrainList?.some((train: any) => 
            train?.tbsAvailability?.some((availability: any) => 
              availability?.confirmationGuaranteeText && availability.confirmationGuaranteeText.trim() !== ''
            )
          ) || false;
          
          const addon_details = [
            {
              addontype: ADDON_TYPE_FREE_CANCELLATION,
              id: ADDON_TYPE_FREE_CANCELLATION,
              currency: CURRENCY_INR,
              includedunits: freeCancellationEnabled ? 1 : 0,
              unitprice: -1.0,
              unittype: UNIT_TYPE_FREE_CANCELLATION,
              heading: HEADING_FREE_CANCELLATION,
              selected: freeCancellationEnabled,
              available: true,
              is_preselected: freeCancellationEnabled,
            },
          ];

          if (hasTripGuarantee) {
            addon_details.push({
              addontype: ADDON_TYPE_TRIP_GUARANTEE,
              id: ADDON_TYPE_TRIP_GUARANTEE,
              currency: CURRENCY_INR,
              includedunits: 0,
              unitprice: -1.0,
              unittype: UNIT_TYPE_TRIP_GUARANTEE,
              heading: HEADING_TRIP_GUARANTEE,
              selected: false,
              available: true,
              is_preselected: false,
            });
          }

          const xdmPayload = {
            ...pdtLoggerObj,
            ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
            page_context: {
              ...pdtLoggerObj.page_context,
              page_type: PAGE_TYPE_FUNNEL,
              funnel_step: PAGE_NAME_LISTING,
              prev_page_name: PAGE_NAME_LANDING,
              lob_category: LOB_RAILS,
              travel_store: {
                store_id: 'MMT',
              },
            },
            
            addon_details,
          };

          await XDMWrapper.logEvent({
            payload: transformPayloadForXDM(xdmPayload),
            schema: RailsLandingPageSchema,
            otherConfig: {
              lobName: LOB_RAILS,
              adobeKeyConfig: [],
            },
            target: TARGETS.ALL,
            extraOptions: {
              lob: LOB_RAILS,
              destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
            },
          });
        } catch (xdmError) {
          console.error('Error sending listing event to XDMWrapper:', xdmError);
        }
    }

    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logging Listing Page Load PDT event', error);
    logGrafanaErrorMetrics(
      PAGE.LISTING_V3,
      ENTITY_TYPE.PAGE,
      ENTITY.LISTING_PDT_ERROR_MEASUREMENT_PLATFORM,
      error,
      NEW_PDT_ERROR_CODES.LISTING_LOAD_ERROR_CODE,
    );
  }
};

export const logTrainCardPdtClickEvents =
  (
    eventValue: string,
    position: number,
    index: number,
    trainNumber: number
  ) =>
  async (dispatch: any, getState: any) => {
    try {
      if (!getRailsListingNewPdtLoggingOnOff()) {
        return null;
      }

      const state = getState() || {};
      const searchContext = await buildEnrichedSearchContext(state);
      const { originStation, destinationStation, departureDate } = searchContext;

      const legs = [];
      if (originStation && destinationStation) {
        const legId = `${trainNumber}_${originStation?.code}_${destinationStation?.code}_${departureDate}`;
        legs.push({
          id: legId,
          number: trainNumber,
          category: LOB_RAILS,
          from_station: {
            code: originStation?.code || null,
            name: originStation?.name || null,
          },
          to_station: {
            code: destinationStation?.code || null,
            name: destinationStation?.name || null,
          },
        });
      }

      const productItem: any = {
        position: {
          v: position,
          h: index,
        },
        train_number: trainNumber,
        legs: legs,
      };

      const eventDetail = {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: COMMON_PDT_CONSTANTS.BUTTON_CLICKED,
        event_type: COMMON_PDT_CONSTANTS.ACTION_EVENT,
        event_value: eventValue,
        interaction_type: 'button_click',
        components: {
          id: 'train_card_click',
          component_type: 'train_card_click',
          product_list: [productItem],
        },
      };

      const pdtLoggerObj = {
        page_context: {
          lob: LOB_RAILS,
          lob_category: LOB_RAILS,
          page_type: PAGE_TYPE_FUNNEL,
          page_name: PAGE_NAME_LISTING,
          funnel_step: PAGE_NAME_LISTING,
        },
        event_tracking_context: {
          ...(await COMMON_EVENT_TRACKING_CONTEXT()),
          template_id: getPdtSchemaId(),
          topic_name: getPdtSchemaName(),
          ...(await getJourneyId(searchContext)),
        },
        search_context: searchContextMapper(searchContext),
        event_detail: eventDetail,
      };

      logRailsEventToPDT(pdtLoggerObj);

      try {
          const xdmPayload = {
            ...pdtLoggerObj,
            ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
            page_context: {
              ...pdtLoggerObj.page_context,
              prev_page_name: PAGE_NAME_LANDING,
              travel_store: {
                store_id: 'MMT',
              },
            },
          };

          await XDMWrapper.logEvent({
            payload: transformPayloadForXDM(xdmPayload),
            schema: RailsLandingPageSchema,
            otherConfig: {
              lobName: LOB_RAILS,
              adobeKeyConfig: [],
            },
            target: TARGETS.ALL,
            extraOptions: {
              lob: LOB_RAILS,
              destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
            },
          });
        } catch (xdmError) {
          console.error('Error sending train card click to XDMWrapper:', xdmError);
        }

      return pdtLoggerObj;
    } catch (e) {
      console.error('Error in logging Listing Click PDT event', e);
    }
  };

export const logConnectedTravelPdtEvent = async (
  eventValue: eventValue,
  ctOptions: Array<object>,
  searchContext: object,
  availabilityBucket: string,
) => {
  try {
    if (!getRailsCtPdtLoggingOnOff()) {
      return null;
    }

    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...CT_PDT_EVENTS?.[eventValue],
      listing_availability_bucket: availabilityBucket || null,
      components: {
        id: 'connected_travel_component',
        component_type: 'connected_travel',
        product_list: constructConnectedTravelPdtData(ctOptions, eventValue),
      },
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: PAGE_NAME_LISTING,
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: getPdtSchemaId(),
        topic_name: getPdtSchemaName(),
        ...(await getJourneyId(searchContext)),
      },
      search_context: searchContextMapper(searchContext),
      event_detail: eventDetail,
    };
    logRailsEventToPDT(pdtLoggerObj);
    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logging Connected Travel PDT event', error);
    logGrafanaErrorMetrics(
      PAGE.LISTING,
      ENTITY_TYPE.PAGE,
      ENTITY.CONNECTED_TRAVEL_ERROR_MEASUREMENT_PLATFORM,
      error,
      NEW_PDT_ERROR_CODES.CONNECTED_TRAVEL_ERROR,
    );
  }
};

const constructConnectedTravelPdtData = (ctOptions: Array<object>, eventValue: string) => {
  const productList = ctOptions?.map((ctItem: any, ctIndex: number) => {
    const layoverDetails =
      Object?.keys(ctItem?.layoverDetails || {})?.length > 0
        ? {
            time: ctItem?.layoverDetails?.['1']?.layoverDuration || null,
            distance: ctItem?.layoverDetails?.['1']?.layoverDistance || null,
          }
        : null;

    const legs = ctItem?.journeyLegs?.map((responseLegItem: any, legIndex: number) => {
      let legItem = {};
      if (responseLegItem?.travelMode === 'TRAIN') {
        legItem = getCtTrainsLegData(responseLegItem, legIndex + 1);
      } else if (responseLegItem?.travelMode === 'BUS') {
        legItem = getCtBusLegData(responseLegItem, legIndex + 1);
      }
      return legItem;
    });

    return {
      id: legs.map((leg: any) => leg?.id).join('|'),
      product_type: 'connected_travel',
      persuasion: ctItem?.optionCategoryType || null,
      duration: ctItem?.durationMins || null,
      total_fare: ctItem?.startingPriceInFloat || null,
      layover_details: layoverDetails,
      rank: eventValue === pdtCtEventValues.EXPANDED ? ctIndex + 1 : null,
      legs,
    };
  });
  return productList;
};

export const logTravellerAdditionalClickEventsWithXDM =
  (
    eventValue: string,
    responseLegItem?: object,
    searchContext?: object,
    addOnDetails?: object,
    selectedData?: object,
    travellerInfo = null,
    travellerInfoHashed = null,
  ) =>
  async (dispatch: any, getState: any) => {
    try {
      const state = getState() || {};
      const { railsListing: { selectedTrainInfo } = {} } = state;
      const finalTrainInfo = responseLegItem || selectedTrainInfo || {};
      const enrichedSearchContext = await buildEnrichedSearchContext(state);
      const finalSearchContext = searchContext || enrichedSearchContext;
      const finalAddOnDetails = addOnDetails || [];
      const finalSelectedData = selectedData || {};

      const XDM_EVENT_MAPPING = {
        TRAVELLER_BOARDING_STATION_CHANGE_CLICK: 'boarding_station_change',
        FREE_CANCELLATION_OPTED: 'free_cancellation_opted',
        FREE_CANCELLATION_DESELECTED: 'free_cancellation_deselected',
        IRCTC_WHY_REQUIRED_CLICKED: 'irctc_why_required',
      };
      const legs = [];
      legs.push(
        getCtTrainsLegDataForTravellers({ trainMetaData: finalTrainInfo }, finalSelectedData, 0),
      );
      const eventDetail = {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        ...TRAVELLER_PDT_EVENTS?.[eventValue as keyof typeof TRAVELLER_PDT_EVENTS],
        components: {
          id: 'traveller_page_component',
          component_type: 'traveller_page',
          product_list: [
            {
              id: legs.map((leg: any) => leg?.id).join('|') || null,
              rank: 1,
              legs: legs || null,
            },
          ],
        },
      };

      const pdtLoggerObj = {
        page_context: {
          lob: LOB_RAILS,
          page_name: 'travellers',
        },
        event_tracking_context: {
          ...(await COMMON_EVENT_TRACKING_CONTEXT()),
          template_id: getPdtSchemaId(),
          topic_name: getPdtSchemaName(),
          ...(await getJourneyId(finalSearchContext)),
        },
        search_context: searchContextMapper(finalSearchContext),
        event_detail: eventDetail,
        addon_details: finalAddOnDetails,
        traveller_info: travellerInfo || null,
      };

      logRailsEventToPDT(pdtLoggerObj);
      const xdmEventValue = XDM_EVENT_MAPPING[eventValue as keyof typeof XDM_EVENT_MAPPING];
      if (xdmEventValue) {
        try {
          const xdmPayload = {
            ...pdtLoggerObj,
            ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
            ...(travellerInfoHashed && { traveller_info: travellerInfoHashed }),
            page_context: {
              ...pdtLoggerObj.page_context,
              page_type: PAGE_TYPE_FUNNEL,
              funnel_step: PAGE_NAME_TRAVELLER,
              prev_page_name: PAGE_NAME_LANDING,
              lob_category: LOB_RAILS,
              travel_store: {
                store_id: 'MMT',
              },
            },
            addon_details: Array.isArray(finalAddOnDetails) ? finalAddOnDetails : [],
          };

          await XDMWrapper.logEvent({
            payload: transformPayloadForXDM(xdmPayload),
            schema: RailsLandingPageSchema,
            otherConfig: {
              lobName: LOB_RAILS,
              adobeKeyConfig: [],
            },
            target: TARGETS.ALL,
            extraOptions: {
              lob: LOB_RAILS,
              destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
            },
          });
        } catch (xdmError) {
          console.error(`Error sending ${eventValue} to XDMWrapper:`, xdmError);
        }
      }

      return pdtLoggerObj;
    } catch (error: any) {
      console.error('Error in logging Traveller Additional Click PDT event:', error);
      logGrafanaErrorMetrics(
        PAGE.TRAVELLER,
        ENTITY_TYPE.PAGE,
        ENTITY.TRAVELLER_PDT_ERROR_MEASUREMENT_PLATFORM,
        error,
        NEW_PDT_ERROR_CODES.TRAVELLER_ERROR_CODE,
      );
    }
  };

export const logTravellersPageLoadPdtEvent = async (
  eventValue: string,
  responseLegItem: object,
  searchContext: object,
  addOnDetails: object,
  selectedData: object,
  travellerInfo = null,
  travellerInfoHashed = null,
) => {
  try {
    const trainCardDetails = await getDataFromStorage(LISTING_PDT_TRAIN_CARD_DETAILS);
    const trainRank = trainCardDetails?.trainPosition || 1;
    
    const legs = [];
    legs.push(getCtTrainsLegDataForTravellers({ trainMetaData: responseLegItem }, selectedData, trainRank));
    
    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...TRAVELLER_PDT_EVENTS?.[eventValue as keyof typeof TRAVELLER_PDT_EVENTS],
      components: {
        id: 'traveller_page_component',
        component_type: 'traveller_page',
        product_list: [
          {
            id: legs.map((leg: any) => leg?.id).join('|') || null,
            rank: trainRank,
            legs: legs || null,
          },
        ],
      },
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: 'travellers',
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: getPdtSchemaId(),
        topic_name: getPdtSchemaName(),
        ...(await getJourneyId(searchContext)),
      },
      search_context: searchContextMapper(searchContext),
      event_detail: eventDetail,
      addon_details: addOnDetails,
      traveller_info: travellerInfo || null,
    };
    logRailsEventToPDT(pdtLoggerObj);
    if (eventValue === 'TRAVELLERS_LOAD') {
      try {
        const xdmPayload = {
          ...pdtLoggerObj,
          ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
          ...(travellerInfoHashed && { traveller_info: travellerInfoHashed }),
          page_context: {
            ...pdtLoggerObj.page_context,
            page_type: PAGE_TYPE_FUNNEL,
            funnel_step: PAGE_NAME_TRAVELLER,
            prev_page_name: PAGE_NAME_LANDING,
            lob_category: LOB_RAILS,
            travel_store: {
              store_id: 'MMT',
            },
          },
          addon_details: Array.isArray(addOnDetails) ? addOnDetails : [],
        };

        await XDMWrapper.logEvent({
          payload: transformPayloadForXDM(xdmPayload),
          schema: RailsLandingPageSchema,
          otherConfig: {
            lobName: LOB_RAILS,
            adobeKeyConfig: [],
          },
          target: TARGETS.ALL,
          extraOptions: {
            lob: LOB_RAILS,
            destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
          },
        });
      } catch (xdmError) {
        console.error('Error sending traveller page load event to XDMWrapper:', xdmError);
      }
    } else if (eventValue === 'TRAVELLERS_ON_REVIEW_CLICK') {
      try {
        const xdmPayload = {
          ...pdtLoggerObj,
          ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
          ...(travellerInfoHashed && { traveller_info: travellerInfoHashed }),
          page_context: {
            ...pdtLoggerObj.page_context,
            page_type: PAGE_TYPE_FUNNEL,
            funnel_step: PAGE_NAME_TRAVELLER,
            prev_page_name: PAGE_NAME_LANDING,
            lob_category: LOB_RAILS,
            travel_store: {
              store_id: 'MMT',
            },
          },
          addon_details: Array.isArray(addOnDetails) ? addOnDetails : [],
        };

        await XDMWrapper.logEvent({
          payload: transformPayloadForXDM(xdmPayload),
          schema: RailsLandingPageSchema,
          otherConfig: {
            lobName: LOB_RAILS,
            adobeKeyConfig: [],
          },
          target: TARGETS.ALL,
          extraOptions: {
            lob: LOB_RAILS,
            destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
          },
        });
      } catch (xdmError) {
        console.error('Error sending proceed to payment event to XDMWrapper:', xdmError);
      }
    }

    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logging Traveller Page PDT event', error);
    logGrafanaErrorMetrics(
      PAGE.TRAVELLER,
      ENTITY_TYPE.PAGE,
      ENTITY.TRAVELLER_PDT_ERROR_MEASUREMENT_PLATFORM,
      error,
      NEW_PDT_ERROR_CODES.TRAVELLER_ERROR_CODE,
    );
  }
};

const getCtTrainsLegDataForTravellers = (
  responseLegItem: object,
  selectedData: object,
  legIndex: number,
) => {
  const trainMetaData = responseLegItem?.trainMetaData;
  const departureDate = new Date(
    trainMetaData?.departureTimeEpochInSec * 1000,
  ).toLocaleDateString();
  const arrivalDate = new Date(trainMetaData?.arrivalTimeEpochInSec * 1000).toLocaleDateString();
  // eslint-ignore-next-line
  const legId = `${trainMetaData?.trainNumber}_${trainMetaData?.frmStnCode}_${trainMetaData?.toStnCode}_${departureDate}}`;
  const catalogList: {
    quota: any;
    class_name: any;
    price: { total_amount: any };
    status: any;
    date: any;
    availability_last_updated: any;
    free_cancellation_available: boolean;
    trip_gaurantee_available: boolean;
  }[] = [];

  catalogList.push({
    quota: selectedData?.quota || null,
    class_name: selectedData?.classCode || null,
    price: {
      total_amount: parseFloat(selectedData?.totalFare || selectedData?.baseFare) || null,
    },
    status: selectedData?.availablityStatus || null,
    date: selectedData?.availablityDate || null,
    availability_last_updated: selectedData?.lastUpdatedOn || null,
    free_cancellation_available: !!selectedData?.freeCancellationText,
    trip_gaurantee_available: !!selectedData?.confirmationGuaranteeText,
    source_of_data: PAGE_NAME_LANDING,
  });
  return {
    id: legId,
    name: trainMetaData?.trainName || null,
    number: trainMetaData?.trainNumber || null,
    fare_shown: parseFloat(selectedData?.totalFare || selectedData?.baseFare) || null,
    product_type: trainMetaData?.trainType?.[0] || null,
    duration: trainMetaData?.duration || null,
    is_cluster: trainMetaData?.clusterTrain ?? null,
    category: LOB_RAILS,
    from_station: {
      code: trainMetaData?.frmStnCode || null,
      name: trainMetaData?.frmStnCity || null,
      departure_time: trainMetaData?.departureTime || null,
      departure_date: departureDate || null,
    },
    to_station: {
      code: trainMetaData?.toStnCode || null,
      name: trainMetaData?.toStnCity || null,
      arrival_time: trainMetaData?.arrivalTime || null,
      arrival_date: arrivalDate || null,
    },
    boarding_station: {
      code: selectedData?.boardingStation?.stationCode || null,
      name: selectedData?.boardingStation?.stationName || null,
      departure_time: trainMetaData?.departureTime || null,
      departure_date: departureDate || null,
    },
    availability_shown: selectedData?.availablityStatus || null,
    catalog_list: catalogList?.length > 0 ? catalogList : null,
    is_dynamic_pricing_applicable: (() => {
      const { trainType, dynamicFareFlag } = trainMetaData || {};
      const dynamicPricingTrainTypes = ['SV', 'R', 'D', 'S'];
      const hasIntersection = trainType?.some(type => dynamicPricingTrainTypes.includes(type)) || false;
      return hasIntersection || dynamicFareFlag === true;
    })(),
    rank: legIndex || null,
  };
};

export const getCtTrainsLegData = (responseLegItem: object, legIndex: number) => {
  const trainMetaData = responseLegItem?.trainMetaData;
  let departureDate = new Date(trainMetaData?.departureTimeEpochInSec * 1000).toLocaleDateString();
  let arrivalDate = new Date(trainMetaData?.arrivalTimeEpochInSec * 1000).toLocaleDateString();
  // eslint-ignore-next-line
  const legId = `${trainMetaData?.trainNumber}_${trainMetaData?.frmStnCode}_${trainMetaData?.toStnCode}_${departureDate}`;
  const catalogList = trainMetaData?.tbsAvailability?.map((tbsInfo) => {
    return {
      quota: tbsInfo?.quota || null,
      class_name: tbsInfo?.classType || null,
      price: {
        total_amount: parseFloat(tbsInfo?.totalFare) || null,
      },
      status: tbsInfo?.availablityStatus || null,
      date: tbsInfo?.availablityDate || null,
      availability_last_updated: tbsInfo?.lastUpdatedOn || null,
      free_cancellation_available: tbsInfo?.freeCancellationText ? true : false,
      trip_gaurantee_available: tbsInfo?.confirmationGuaranteeText ? true : false,
    };
  });
  return {
    id: legId,
    name: trainMetaData?.trainName || null,
    number: trainMetaData?.trainNumber || null,
    fare_shown: responseLegItem?.minimum_fare || null,
    product_type: trainMetaData?.trainType?.[0] || null,
    duration: trainMetaData?.duration || null,
    is_cluster: trainMetaData?.clusterTrain ?? null,
    category: LOB_RAILS,
    from_station: {
      code: trainMetaData?.frmStnCode || null,
      name: trainMetaData?.frmStnCity || null,
      departure_time: trainMetaData?.departureTime || null,
      departure_date: departureDate || null,
    },
    to_station: {
      code: trainMetaData?.toStnCode || null,
      name: trainMetaData?.toStnCity || null,
      arrival_time: trainMetaData?.arrivalTime || null,
      arrival_date: arrivalDate || null,
    },
    catalog_list: catalogList?.length > 0 ? catalogList : null,
    rank: responseLegItem?.legNo || legIndex || null,
  };
};

const getCtBusLegData = (responseLegItem: object, legIndex: number) => {
  const busMetaData = responseLegItem?.busMetaData;
  return {
    id: busMetaData?.trip_key || null,
    name: busMetaData?.vendor_operator_name || null,
    number: busMetaData?.vendor_trip_id || null,
    fare_shown: busMetaData?.minimum_base_fare,
    product_type: busMetaData?.mmt_bus_type_name || null,
    category: 'bus',
    duration: busMetaData?.journey_time_in_minutes || null,
    from_station: {
      code: busMetaData?.mmt_source_city_code || null,
      name: busMetaData?.mmt_source_city_name || null,
      departure_time: busMetaData?.departure_time || null,
      departure_date: busMetaData?.departure_date || null,
    },
    to_station: {
      code: busMetaData?.mmt_destination_city_code || null,
      name: busMetaData?.mmt_destination_city_name || null,
      arrival_time: busMetaData?.arrival_time || null,
      arrival_date: busMetaData?.arrival_date || null,
    },
    availability_shown: busMetaData?.availableSeats || null,
    rank: responseLegItem?.legNo || legIndex || null,
  };
};

const calculateAdvancePurchaseWindow = (departureDate: string): number => {
  try {
    if (!departureDate) {
      return 0;
    }

    const today = new Date().getTime();
    const departure = new Date(departureDate).getTime();

    if (isNaN(departure) || isNaN(today)) {
      return 0;
    }

    const days = Math.max(0, Math.ceil((departure - today) / (1000 * 60 * 60 * 24)));

    return isNaN(days) ? 0 : days;
  } catch (error) {
    return 0;
  }
};

const buildFiltersSeenArray = (filterObject: any) => {
  const seenFilters = [];
  
  if (!filterObject) {
    return [{
      category: 'listing_filters',
      group: 'default',
      source: 'listing_page',
      value: 'no_filters_available',
      range_filter: false,
    }];
  }

  Object.keys(filterObject).forEach((filterCategory) => {
    const categoryData = filterObject[filterCategory];
    
    if (categoryData?.filterTypes && Object.keys(categoryData.filterTypes).length > 0) {
      Object.keys(categoryData.filterTypes).forEach((filterKey) => {
        const filterData = categoryData.filterTypes[filterKey];
        
        seenFilters.push({
          category: filterCategory,
          group: filterData.heading || filterKey,
          source: 'listing_page',
          value: filterData.subHeading || filterData.heading || filterKey,
          range_filter: filterCategory.includes('Time') || filterCategory.includes('time') || filterCategory.includes('range'),
        });
      });
    }
  });

  if (seenFilters.length === 0) {
    seenFilters.push({
      category: 'no_filters_available',
      group: 'default',
      source: 'listing_page',
      value: 'no_filter_options',
      range_filter: false,
    });
  }

  return seenFilters;
};

export const searchContextMapper = (searchContext: Object) => {
  const {
    originStation = {},
    destinationStation = {},
    departureDate = '',
    pax = {},
  } = searchContext || {};

  const paxDetails = {
    child: pax.child || { count: 0, ages: [] },
    infant: pax.infant || 0,
    adult: pax.adult || 0,
  };
  const advancePurchaseWindow = calculateAdvancePurchaseWindow(departureDate);

  return {
    to: {
      locus: {
        locus_id: destinationStation?.locusCode || '',
        locus_type: destinationStation?.locusType || '',
        locus_v2_id: destinationStation?.locusV2Id || '',
        locus_v2_type: destinationStation?.locusV2Type || '',
        country: 'India',
      },
      location: {
        code: destinationStation?.code || '',
        name: destinationStation?.stationName || '',
        country_code: 'IN',
        country: 'India',
      },
    },
    from: {
      locus: {
        locus_id: originStation?.locusCode || '',
        locus_type: originStation?.locusType || '',
        locus_v2_id: originStation?.locusV2Id || '',
        locus_v2_type: originStation?.locusV2Type || '',
        country: 'India',
      },
      location: {
        code: originStation?.code || '',
        name: originStation?.stationName || '',
        country_code: 'IN',
        country: 'India',
      },
    },
    from_date_time: departureDate ? Date.parse(departureDate) : null,
    travel_class: 'All',
    pax: {
      count: paxDetails.adult + paxDetails.child.count + paxDetails.infant,
      details: {
        child: {
          count: paxDetails.child.count,
          ages: paxDetails.child.ages,
        },
        adult: {
          count: paxDetails.adult,
        },
        infant: {
          count: paxDetails.infant,
        },
      },
    },
    advance_purchase: isNaN(advancePurchaseWindow) ? '0' : String(advancePurchaseWindow),
  };
};

export const logListingClickEvent = (eventName: string) => async (dispatch: any, getState: any) => {
  try {
    if (!getRailsListingNewPdtLoggingOnOff()) {
      return null;
    }

    const state = getState() || {};
    const enrichedSearchContext = await buildEnrichedSearchContext(state);

    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      event_name: eventName,
      event_type: COMMON_PDT_CONSTANTS.ACTION_EVENT,
      event_value: eventName,
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        lob_category: LOB_RAILS,
        page_type: PAGE_TYPE_FUNNEL,
        page_name: PAGE_NAME_LISTING,
        funnel_step: PAGE_NAME_LISTING,
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: getPdtSchemaId(),
        topic_name: getPdtSchemaName(),
        ...(await getJourneyId(enrichedSearchContext)),
      },
      search_context: searchContextMapper(enrichedSearchContext),
      event_detail: eventDetail,
    };

    logRailsEventToPDT(pdtLoggerObj);

    try {
        const xdmPayload = {
          ...pdtLoggerObj,
          ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
          page_context: {
            ...pdtLoggerObj.page_context,
            prev_page_name: PAGE_NAME_LANDING,
            travel_store: {
              store_id: 'MMT',
            },
          },
        };

        await XDMWrapper.logEvent({
          payload: transformPayloadForXDM(xdmPayload),
          schema: RailsLandingPageSchema,
          otherConfig: {
            lobName: LOB_RAILS,
            adobeKeyConfig: [],
          },
          target: TARGETS.ALL,
          extraOptions: {
            lob: LOB_RAILS,
            destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
          },
        });
      } catch (xdmError) {
        console.error('Error sending listing click to XDMWrapper:', xdmError);
      }
    
    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logging Listing Click event', error);
  }
};

export const logIrctcWhyRequiredClickEvent = () => async (dispatch: any, getState: any) => {
  try {
    const state = getState() || {};
    const enrichedSearchContext = await buildEnrichedSearchContext(state);
    const addOnDetails = (await getDataFromStorage(TRAVELLER_PDT_ADDON_DETAILS)) || [];

    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...TRAVELLER_PDT_EVENTS?.['IRCTC_WHY_REQUIRED_CLICKED' as keyof typeof TRAVELLER_PDT_EVENTS],
      components: {
        id: 'traveller_page_component',
        component_type: 'traveller_page',
      },
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: 'travellers',
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: getPdtSchemaId(),
        topic_name: getPdtSchemaName(),
        ...(await getJourneyId(enrichedSearchContext)),
      },
      search_context: searchContextMapper(enrichedSearchContext),
      event_detail: eventDetail,
      addon_details: addOnDetails,
    };

    logRailsEventToPDT(pdtLoggerObj);

    try {
      const xdmPayload = {
        ...pdtLoggerObj,
        ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
        page_context: {
          ...pdtLoggerObj.page_context,
          page_type: PAGE_TYPE_FUNNEL,
          funnel_step: PAGE_NAME_TRAVELLER,
          prev_page_name: PAGE_NAME_LANDING,
          lob_category: LOB_RAILS,
          travel_store: {
            store_id: 'MMT',
          },
        },
        addon_details: Array.isArray(addOnDetails) ? addOnDetails : [],
      };

      await XDMWrapper.logEvent({
        payload: transformPayloadForXDM(xdmPayload),
        schema: RailsLandingPageSchema,
        otherConfig: {
          lobName: LOB_RAILS,
          adobeKeyConfig: [],
        },
        target: TARGETS.ALL,
        extraOptions: {
          lob: LOB_RAILS,
          destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
        },
      });
    } catch (xdmError) {
      console.error('Error sending IRCTC_WHY_REQUIRED_CLICKED to XDMWrapper:', xdmError);
    }

    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logIrctcWhyRequiredClickEvent:', error);
  }
};

export const logIrctcUsernameChangeClickEvent = () => async (dispatch: any, getState: any) => {
  try {
    const state = getState() || {};
    const enrichedSearchContext = await buildEnrichedSearchContext(state);
    const addOnDetails = (await getDataFromStorage(TRAVELLER_PDT_ADDON_DETAILS)) || [];

    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...TRAVELLER_PDT_EVENTS?.[
        'IRCTC_USERNAME_CHANGE_CLICKED' as keyof typeof TRAVELLER_PDT_EVENTS
      ],
      components: {
        id: 'traveller_page_component',
        component_type: 'traveller_page',
      },
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: 'travellers',
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: getPdtSchemaId(),
        topic_name: getPdtSchemaName(),
        ...(await getJourneyId(enrichedSearchContext)),
      },
      search_context: searchContextMapper(enrichedSearchContext),
      event_detail: eventDetail,
      addon_details: addOnDetails,
    };

    logRailsEventToPDT(pdtLoggerObj);

    try {
      const xdmPayload = {
        ...pdtLoggerObj,
        ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
        page_context: {
          ...pdtLoggerObj.page_context,
          page_type: PAGE_TYPE_FUNNEL,
          funnel_step: PAGE_NAME_TRAVELLER,
          prev_page_name: PAGE_NAME_LANDING,
          lob_category: LOB_RAILS,
          travel_store: {
            store_id: 'MMT',
          },
        },
        addon_details: Array.isArray(addOnDetails) ? addOnDetails : [],
      };

      await XDMWrapper.logEvent({
        payload: transformPayloadForXDM(xdmPayload),
        schema: RailsLandingPageSchema,
        otherConfig: {
          lobName: LOB_RAILS,
          adobeKeyConfig: [],
        },
        target: TARGETS.ALL,
        extraOptions: {
          lob: LOB_RAILS,
          destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
        },
      });
    } catch (xdmError) {
      console.error('Error sending IRCTC_USERNAME_CHANGE_CLICKED to XDMWrapper:', xdmError);
    }

    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logIrctcUsernameChangeClickEvent:', error);
  }
};

export const logCouponApplyClickEvent = () => async (dispatch: any, getState: any) => {
  try {
    const state = getState() || {};
    const enrichedSearchContext = await buildEnrichedSearchContext(state);
    const addOnDetails = (await getDataFromStorage(TRAVELLER_PDT_ADDON_DETAILS)) || [];

    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...TRAVELLER_PDT_EVENTS?.['COUPON_APPLY_CLICKED' as keyof typeof TRAVELLER_PDT_EVENTS],
      components: {
        id: 'traveller_page_component',
        component_type: 'traveller_page',
      },
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: 'travellers',
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: getPdtSchemaId(),
        topic_name: getPdtSchemaName(),
        ...(await getJourneyId(enrichedSearchContext)),
      },
      search_context: searchContextMapper(enrichedSearchContext),
      event_detail: eventDetail,
      addon_details: addOnDetails,
    };

    logRailsEventToPDT(pdtLoggerObj);

    try {
      const xdmPayload = {
        ...pdtLoggerObj,
        ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
        page_context: {
          ...pdtLoggerObj.page_context,
          page_type: PAGE_TYPE_FUNNEL,
          funnel_step: PAGE_NAME_TRAVELLER,
          prev_page_name: PAGE_NAME_LANDING,
          lob_category: LOB_RAILS,
          travel_store: {
            store_id: 'MMT',
          },
        },
        addon_details: Array.isArray(addOnDetails) ? addOnDetails : [],
      };

      await XDMWrapper.logEvent({
        payload: transformPayloadForXDM(xdmPayload),
        schema: RailsLandingPageSchema,
        otherConfig: {
          lobName: LOB_RAILS,
          adobeKeyConfig: [],
        },
        target: TARGETS.ALL,
        extraOptions: {
          lob: LOB_RAILS,
          destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
        },
      });
    } catch (xdmError) {
      console.log('Error sending COUPON_APPLY_CLICKED to XDMWrapper:', xdmError);
    }

    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logCouponApplyClickEvent:', error);
  }
};

export const logCouponRemoveClickEvent = () => async (dispatch: any, getState: any) => {
  try {
    const state = getState() || {};
    const enrichedSearchContext = await buildEnrichedSearchContext(state);
    const addOnDetails = (await getDataFromStorage(TRAVELLER_PDT_ADDON_DETAILS)) || [];

    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...TRAVELLER_PDT_EVENTS?.['COUPON_REMOVE_CLICKED' as keyof typeof TRAVELLER_PDT_EVENTS],
      components: {
        id: 'traveller_page_component',
        component_type: 'traveller_page',
      },
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: 'travellers',
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: getPdtSchemaId(),
        topic_name: getPdtSchemaName(),
        ...(await getJourneyId(enrichedSearchContext)),
      },
      search_context: searchContextMapper(enrichedSearchContext),
      event_detail: eventDetail,
      addon_details: addOnDetails,
    };

    logRailsEventToPDT(pdtLoggerObj);

    try {
      const xdmPayload = {
        ...pdtLoggerObj,
        ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
        page_context: {
          ...pdtLoggerObj.page_context,
          page_type: PAGE_TYPE_FUNNEL,
          funnel_step: PAGE_NAME_TRAVELLER,
          prev_page_name: PAGE_NAME_LANDING,
          lob_category: LOB_RAILS,
          travel_store: {
            store_id: 'MMT',
          },
        },
        addon_details: Array.isArray(addOnDetails) ? addOnDetails : [],
      };

      await XDMWrapper.logEvent({
        payload: transformPayloadForXDM(xdmPayload),
        schema: RailsLandingPageSchema,
        otherConfig: {
          lobName: LOB_RAILS,
          adobeKeyConfig: [],
        },
        target: TARGETS.ALL,
        extraOptions: {
          lob: LOB_RAILS,
          destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
        },
      });
    } catch (xdmError) {
      console.error('Error sending COUPON_REMOVE_CLICKED to XDMWrapper:', xdmError);
    }

    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logCouponRemoveClickEvent:', error);
  }
};

export const logFreeCancellationClickEvent = (eventValue: string) => async (dispatch: any, getState: any) => {
  try {
    const state = getState() || {};
    const enrichedSearchContext = await buildEnrichedSearchContext(state);
    const addOnDetails = (await getDataFromStorage(TRAVELLER_PDT_ADDON_DETAILS)) || [];

    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...TRAVELLER_PDT_EVENTS?.[eventValue as keyof typeof TRAVELLER_PDT_EVENTS],
      components: {
        id: 'traveller_page_component',
        component_type: 'traveller_page',
      },
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: 'travellers',
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: getPdtSchemaId(),
        topic_name: getPdtSchemaName(),
        ...(await getJourneyId(enrichedSearchContext)),
      },
      search_context: searchContextMapper(enrichedSearchContext),
      event_detail: eventDetail,
      addon_details: addOnDetails,
    };

    logRailsEventToPDT(pdtLoggerObj);

    const xdmEventValue =
      eventValue === 'FREE_CANCELLATION_OPTED'
        ? 'free_cancellation_opted'
        : 'free_cancellation_deselected';

    const xdmPayload = {
      ...pdtLoggerObj,
      ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
      page_context: {
        ...pdtLoggerObj.page_context,
        page_type: PAGE_TYPE_FUNNEL,
        funnel_step: PAGE_NAME_TRAVELLER,
        prev_page_name: PAGE_NAME_LANDING,
        lob_category: LOB_RAILS,
        travel_store: {
          store_id: 'MMT',
        },
      },
      addon_details: Array.isArray(addOnDetails) ? addOnDetails : [],
    };

    await XDMWrapper.logEvent({
      payload: transformPayloadForXDM(xdmPayload),
      schema: RailsLandingPageSchema,
      otherConfig: {
        lobName: LOB_RAILS,
        adobeKeyConfig: [],
      },
      target: TARGETS.ALL,
      extraOptions: {
        lob: LOB_RAILS,
        destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
      },
    });

    return pdtLoggerObj;
  } catch (error: any) {
    if (error?.message?.includes('XDM')) {
      console.error(`Error sending ${eventValue} to XDMWrapper:`, error);
    }
  }
};

export const logAdditionalPreferencesClickEvent = () => async (dispatch: any, getState: any) => {
  try {
    const state = getState() || {};
    const enrichedSearchContext = await buildEnrichedSearchContext(state);
    const addOnDetails = (await getDataFromStorage(TRAVELLER_PDT_ADDON_DETAILS)) || [];

    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...TRAVELLER_PDT_EVENTS?.[
        'ADDITIONAL_PREFERENCES_CLICKED' as keyof typeof TRAVELLER_PDT_EVENTS
      ],
      components: {
        id: 'traveller_page_component',
        component_type: 'traveller_page',
      },
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: 'travellers',
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: getPdtSchemaId(),
        topic_name: getPdtSchemaName(),
        ...(await getJourneyId(enrichedSearchContext)),
      },
      search_context: searchContextMapper(enrichedSearchContext),
      event_detail: eventDetail,
      addon_details: addOnDetails,
    };

    logRailsEventToPDT(pdtLoggerObj);

    try {
      const xdmPayload = {
        ...pdtLoggerObj,
        ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
        page_context: {
          ...pdtLoggerObj.page_context,
          page_type: PAGE_TYPE_FUNNEL,
          funnel_step: PAGE_NAME_TRAVELLER,
          prev_page_name: PAGE_NAME_LANDING,
          lob_category: LOB_RAILS,
          travel_store: {
            store_id: 'MMT',
          },
        },
        addon_details: Array.isArray(addOnDetails) ? addOnDetails : [],
      };

      await XDMWrapper.logEvent({
        payload: transformPayloadForXDM(xdmPayload),
        schema: RailsLandingPageSchema,
        otherConfig: {
          lobName: LOB_RAILS,
          adobeKeyConfig: [],
        },
        target: TARGETS.ALL,
        extraOptions: {
          lob: LOB_RAILS,
          destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
        },
      });
    } catch (xdmError) {
      console.error('Error sending ADDITIONAL_PREFERENCES_CLICKED to XDMWrapper:', xdmError);
    }

    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logAdditionalPreferencesClickEvent:', error);
  }
};